import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DENIED_CONSENT,
  GRANTED_CONSENT,
  applyStoredConsent,
  readStoredConsent,
  saveConsent,
} from "@/lib/consent";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/i18n";

const OPEN_PREFS_EVENT = "litiosur:open-cookie-preferences";

export const openCookiePreferences = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_PREFS_EVENT));
};

const CookieConsent = () => {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState(DENIED_CONSENT);

  useEffect(() => {
    const stored = applyStoredConsent();
    if (stored) {
      setDraft({
        necessary: true,
        analytics: stored.analytics,
        ads: stored.ads,
        personalization: stored.personalization,
      });
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const stored = readStoredConsent();
      setDraft(
        stored
          ? {
              necessary: true,
              analytics: stored.analytics,
              ads: stored.ads,
              personalization: stored.personalization,
            }
          : DENIED_CONSENT,
      );
      setModalOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, handler);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, handler);
  }, []);

  const commit = useCallback((choice) => {
    saveConsent(choice);
    setDraft({ ...choice, necessary: true });
    setVisible(false);
    setModalOpen(false);
  }, []);

  const langPrefix = SUPPORTED_LANGUAGES.includes(i18n.language)
    ? i18n.language
    : DEFAULT_LANGUAGE;

  const categories = [
    {
      key: "necessary",
      title: t("cookies_banner.modal.categories.necessary.title"),
      text: t("cookies_banner.modal.categories.necessary.text"),
      locked: true,
      testId: "cookie-pref-necessary-switch",
    },
    {
      key: "analytics",
      title: t("cookies_banner.modal.categories.analytics.title"),
      text: t("cookies_banner.modal.categories.analytics.text"),
      locked: false,
      testId: "cookie-pref-analytics-switch",
    },
    {
      key: "ads",
      title: t("cookies_banner.modal.categories.ads.title"),
      text: t("cookies_banner.modal.categories.ads.text"),
      locked: false,
      testId: "cookie-pref-ads-switch",
    },
    {
      key: "personalization",
      title: t("cookies_banner.modal.categories.personalization.title"),
      text: t("cookies_banner.modal.categories.personalization.text"),
      locked: false,
      testId: "cookie-pref-personalization-switch",
    },
  ];

  return (
    <>
      {visible && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-5 sm:pb-5"
          role="region"
          aria-label={t("cookies_banner.title")}
          data-testid="cookie-banner"
        >
          <div className="mx-auto w-full max-w-3xl rounded-xl border border-border bg-card p-5 shadow-[0_14px_40px_hsl(214_30%_10%/0.16)] sm:p-6">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--terracotta)/0.12)]">
                <Cookie className="h-4.5 w-4.5 text-[hsl(var(--terracotta))]" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-foreground">
                  {t("cookies_banner.title")}
                </h2>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {t("cookies_banner.text")}
                </p>
                <Link
                  to={`/${langPrefix}/cookie-policy`}
                  className="mt-2 inline-block text-sm font-medium text-[hsl(var(--deep-blue))] underline underline-offset-4 transition-colors hover:text-[hsl(var(--terracotta))]"
                  data-testid="cookie-banner-policy-link"
                >
                  {t("cookies_banner.policy_link")}
                </Link>
              </div>
            </div>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                type="button"
                onClick={() => commit(GRANTED_CONSENT)}
                className="h-10 flex-1 rounded-lg"
                data-testid="cookie-banner-accept-all"
              >
                {t("cookies_banner.accept")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => commit(DENIED_CONSENT)}
                className="h-10 flex-1 rounded-lg"
                data-testid="cookie-banner-reject-all"
              >
                {t("cookies_banner.reject")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setModalOpen(true)}
                className="h-10 flex-1 rounded-lg text-[hsl(var(--ink-2))]"
                data-testid="cookie-banner-manage-preferences"
              >
                <SlidersHorizontal className="mr-1.5 h-4 w-4" />
                {t("cookies_banner.manage")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="max-h-[88vh] overflow-y-auto sm:max-w-lg"
          data-testid="cookie-preferences-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              {t("cookies_banner.modal.title")}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              {t("cookies_banner.modal.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-1 space-y-1">
            {categories.map((cat, index) => (
              <div key={cat.key}>
                {index > 0 && <Separator className="my-1" />}
                <div className="flex items-start justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      {cat.title}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {cat.text}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Switch
                      checked={cat.locked ? true : draft[cat.key]}
                      disabled={cat.locked}
                      onCheckedChange={(value) =>
                        setDraft((prev) => ({ ...prev, [cat.key]: value }))
                      }
                      aria-label={cat.title}
                      data-testid={cat.testId}
                    />
                    {cat.locked && (
                      <span className="font-data text-[9px] uppercase tracking-wider text-[hsl(var(--ink-3))]">
                        {t("cookies_banner.modal.always_on")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="mt-2 flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => commit(DENIED_CONSENT)}
              className="h-10 rounded-lg sm:flex-1"
              data-testid="cookie-pref-reject-all-button"
            >
              {t("cookies_banner.modal.reject_all")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => commit(GRANTED_CONSENT)}
              className="h-10 rounded-lg sm:flex-1"
              data-testid="cookie-pref-accept-all-button"
            >
              {t("cookies_banner.modal.accept_all")}
            </Button>
            <Button
              type="button"
              onClick={() => commit(draft)}
              className="h-10 rounded-lg sm:flex-1"
              data-testid="cookie-pref-save-button"
            >
              {t("cookies_banner.modal.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
